import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";

interface FullscreenContextType {
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  isFullscreenSupported: boolean;
}

const FullscreenContext = createContext<FullscreenContextType | null>(null);

function isFullscreenSupported(): boolean {
  if (typeof document === "undefined") return false;
  return !!(
    document.documentElement.requestFullscreen ||
    (document.documentElement as unknown as { webkitRequestFullscreen?: () => void })
      .webkitRequestFullscreen ||
    (document.documentElement as unknown as { msRequestFullscreen?: () => void })
      .msRequestFullscreen
  );
}

function getIsFullscreen(): boolean {
  if (typeof document === "undefined") return false;
  return !!(
    document.fullscreenElement ||
    (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement ||
    (document as unknown as { msFullscreenElement?: Element }).msFullscreenElement
  );
}

async function requestFullscreen(): Promise<void> {
  const elem = document.documentElement;
  if (elem.requestFullscreen) {
    await elem.requestFullscreen();
  } else if (
    (elem as unknown as { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen
  ) {
    await (
      elem as unknown as { webkitRequestFullscreen: () => Promise<void> }
    ).webkitRequestFullscreen();
  } else if (
    (elem as unknown as { msRequestFullscreen?: () => Promise<void> }).msRequestFullscreen
  ) {
    await (elem as unknown as { msRequestFullscreen: () => Promise<void> }).msRequestFullscreen();
  }
}

async function exitFullscreenMode(): Promise<void> {
  if (document.exitFullscreen) {
    await document.exitFullscreen();
  } else if (
    (document as unknown as { webkitExitFullscreen?: () => Promise<void> }).webkitExitFullscreen
  ) {
    await (
      document as unknown as { webkitExitFullscreen: () => Promise<void> }
    ).webkitExitFullscreen();
  } else if ((document as unknown as { msExitFullscreen?: () => Promise<void> }).msExitFullscreen) {
    await (document as unknown as { msExitFullscreen: () => Promise<void> }).msExitFullscreen();
  }
}

interface FullscreenProviderProps {
  children: ReactNode;
}

export function FullscreenProvider({ children }: FullscreenProviderProps) {
  const [isFullscreen, setIsFullscreen] = useState(getIsFullscreen);
  const supported = isFullscreenSupported();

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(getIsFullscreen());
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  }, []);

  const enterFullscreen = useCallback(async () => {
    if (!supported) return;
    try {
      await requestFullscreen();
    } catch (error) {
      console.error("Failed to enter fullscreen:", error);
    }
  }, [supported]);

  const exitFullscreen = useCallback(async () => {
    if (!supported) return;
    try {
      await exitFullscreenMode();
    } catch (error) {
      console.error("Failed to exit fullscreen:", error);
    }
  }, [supported]);

  const toggleFullscreen = useCallback(async () => {
    if (isFullscreen) {
      await exitFullscreen();
    } else {
      await enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  const value = useMemo<FullscreenContextType>(
    () => ({
      isFullscreen,
      toggleFullscreen,
      enterFullscreen,
      exitFullscreen,
      isFullscreenSupported: supported,
    }),
    [isFullscreen, toggleFullscreen, enterFullscreen, exitFullscreen, supported],
  );

  return <FullscreenContext.Provider value={value}>{children}</FullscreenContext.Provider>;
}

export function useFullscreen(): FullscreenContextType {
  const context = useContext(FullscreenContext);
  if (!context) {
    throw new Error("useFullscreen must be used within a FullscreenProvider");
  }
  return context;
}

const defaultFullscreen: FullscreenContextType = {
  isFullscreen: false,
  toggleFullscreen: () => {},
  enterFullscreen: () => {},
  exitFullscreen: () => {},
  isFullscreenSupported: false,
};

/** Safe variant that returns defaults when outside a FullscreenProvider (e.g. tests). */
export function useOptionalFullscreen(): FullscreenContextType {
  const context = useContext(FullscreenContext);
  return context ?? defaultFullscreen;
}
